import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { FlexColumn } from 'ui-components/components/flex/column';
import { FlexRow } from 'ui-components/components/flex/row';
import { Gap } from 'ui-components/components/flex/gap';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { Text, TextType } from 'ui-components/components/text';
import { noop } from 'ui-components/default-props/noop';

import { US } from '../../../constants/country-codes';
import { CarrierConnectionInteractionAction, CarrierConnectionInteractionSection, Workflows } from '../../../tracking/tracking-constants';
import { useTrackWorkflow, WorkflowSteps } from '../../../tracking/use-workflow-tracking';
import { useYupFormValidation } from '../../../validation/use-yup-form-validation';
import type { ValidationError } from '../../../validation/validation-error';
import { messages as rootMessages } from '../messages';
import type { WwexFormData } from '../types';
import { buildAccountInfoSchema } from '../wwex-form-validation-schema';

import { UserInfoForm } from './user-info-form';
import { WwexClearAllLink } from './wwex-clear-all-link';
import { messages } from './edit-account-info-view.messages';
import { WwexSubViewFooter } from './wwex-sub-view-footer';
import { WwexSubViewHeader } from './wwex-sub-view-header';
import style from './edit-account-info-view.module.css';
import modalStyle from '../wwex-registration-modal.module.less';

const EMPTY_ACCOUNT_INFO: Pick<WwexFormData, 'contactFullName' | 'companyName' | 'email' | 'phoneNumber'> = {
  contactFullName: '',
  companyName: '',
  email: '',
  phoneNumber: '',
};

const ACCOUNT_INFO_FIELDS = ['contactFullName', 'companyName', 'email', 'phoneNumber'] as const;

type EditAccountInfoViewProps = {
  headerId: string;
  formData: Partial<WwexFormData>;
  isDisabled: boolean;
  sellerCountry?: string;
  onSave: (data: Partial<WwexFormData>) => void;
  onBack: () => void;
  onCancel: () => void;
};

export const EditAccountInfoView = ({
  headerId,
  formData,
  isDisabled,
  sellerCountry,
  onSave,
  onBack,
  onCancel,
}: EditAccountInfoViewProps) => {
  const [draft, setDraft] = useState({
    contactFullName: formData.contactFullName ?? '',
    companyName: formData.companyName ?? '',
    email: formData.email ?? '',
    phoneNumber: formData.phoneNumber ?? '',
  });
  const [showAllErrors, setShowAllErrors] = useState(false);

  const trackCarrierConnectionStep = useTrackWorkflow(Workflows.CARRIER_CONNECTION_FLOW);
  const initialDraftRef = useRef(draft);
  const editCountRef = useRef(0);

  const schema = useMemo(() => buildAccountInfoSchema(sellerCountry ?? US), [sellerCountry]);
  const { errors, isValid } = useYupFormValidation({ form: draft, schema });

  useEffect(() => {
    const prefillWasEmpty = ACCOUNT_INFO_FIELDS.every((field) => !initialDraftRef.current[field]);
    trackCarrierConnectionStep(WorkflowSteps.INTERACTION, {
      section: CarrierConnectionInteractionSection.ACCOUNT_INFO,
      action: CarrierConnectionInteractionAction.EDIT_OPENED,
      prefill_was_empty: prefillWasEmpty,
      // useYupFormValidation's errors are computed in its own effect, so they're not ready yet on
      // this same mount -- check the prefill against the schema directly instead of waiting for them.
      has_initial_errors: !schema.isValidSync(initialDraftRef.current),
    });
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- fire once when this sub-view mounts
  }, []);

  const handleFieldChange = useCallback((field: string, value: string) => {
    editCountRef.current += 1;
    setDraft((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleClearAll = useCallback(() => {
    trackCarrierConnectionStep(WorkflowSteps.INTERACTION, {
      section: CarrierConnectionInteractionSection.ACCOUNT_INFO,
      action: CarrierConnectionInteractionAction.CLEARED_ALL,
    });
    setDraft(EMPTY_ACCOUNT_INFO);
  }, [trackCarrierConnectionStep]);

  const handleSave = useCallback(() => {
    if (!isValid) {
      setShowAllErrors(true);
      return;
    }

    const fieldsChanged = ACCOUNT_INFO_FIELDS.filter((field) => draft[field] !== initialDraftRef.current[field]);
    trackCarrierConnectionStep(WorkflowSteps.INTERACTION, {
      section: CarrierConnectionInteractionSection.ACCOUNT_INFO,
      action: CarrierConnectionInteractionAction.SAVED,
      fields_changed: fieldsChanged,
      edit_count: editCountRef.current,
    });

    onSave({ ...formData, ...draft });
  }, [draft, formData, isValid, onSave, trackCarrierConnectionStep]);

  const handleCancel = useCallback(() => {
    trackCarrierConnectionStep(WorkflowSteps.INTERACTION, {
      section: CarrierConnectionInteractionSection.ACCOUNT_INFO,
      action: CarrierConnectionInteractionAction.CANCELLED,
    });
    onCancel();
  }, [onCancel, trackCarrierConnectionStep]);

  const handleBack = useCallback(() => {
    trackCarrierConnectionStep(WorkflowSteps.INTERACTION, {
      section: CarrierConnectionInteractionSection.ACCOUNT_INFO,
      action: CarrierConnectionInteractionAction.WENT_BACK,
    });
    onBack();
  }, [onBack, trackCarrierConnectionStep]);

  return (
    <>
      <ModalHeader className={modalStyle.modalHeader} closeModal={handleCancel} id={headerId}>
        <WwexSubViewHeader isDisabled={isDisabled} onBack={handleBack} titleMessage={rootMessages.editAccountInfoTitle} />
      </ModalHeader>

      <ModalBody className={modalStyle.modalBody}>
        <FlexColumn gap={Gap.Space16}>
          <FlexRow className={style.sectionHeaderRow} gap={Gap.Space8}>
            <Text textType={TextType.H4} value={messages.sectionTitle} />
            <WwexClearAllLink isDisabled={isDisabled} onClick={handleClearAll} />
          </FlexRow>

          <UserInfoForm
            companyName={draft.companyName}
            contactFullName={draft.contactFullName}
            email={draft.email}
            errors={errors as Record<string, ValidationError>}
            isDisabled={isDisabled}
            onBlur={noop}
            onChange={handleFieldChange}
            phoneNumber={draft.phoneNumber}
            showAllErrors={showAllErrors}
          />
        </FlexColumn>
      </ModalBody>

      <ModalFooter>
        <WwexSubViewFooter
          isDisabled={isDisabled}
          isSaveDisabled={!isValid}
          onBack={handleBack}
          onCancel={handleCancel}
          onSave={handleSave}
        />
      </ModalFooter>
    </>
  );
};
